import { PERMISSIONS, PermissionKey } from "./permissions";
import { Role } from "./roles";

const LEGACY_PERMISSION_KEYS: PermissionKey[] = [
  PERMISSIONS.WORKLIST_ROUTE,
  PERMISSIONS.PATIENT_ROUTE,
  PERMISSIONS.REPORT_ROUTE,
  PERMISSIONS.TRANSFER_WOUND_ASSESSMENT_ROUTE,
  PERMISSIONS.MERGE_WOUND_ASSESSMENT_ROUTE,
  PERMISSIONS.ADMIN_ROUTE,
  PERMISSIONS.PATIENT,
  PERMISSIONS.ADD_PATIENT,
  PERMISSIONS.ASSESSMENT_THREE_DOT_MENU,
  PERMISSIONS.ASSESSMENT,
  PERMISSIONS.CHARACTERISTIC,
  PERMISSIONS.MEASUREMENT,
  PERMISSIONS.MEASUREMENT_BUTTON,
  PERMISSIONS.TREATMENT,
  PERMISSIONS.TREATMENT_SEARCH,
  PERMISSIONS.TREATMENT_ADD_INTERVENTION,
  PERMISSIONS.NOTES,
  PERMISSIONS.CUSTOM_LABEL,
  PERMISSIONS.SIGN,
  PERMISSIONS.LOCK,
  PERMISSIONS.EXT_SIGN_LOCK,
  PERMISSIONS.RE_EVAL,
  PERMISSIONS.ADMIN,
  PERMISSIONS.USER_PROFILE,
  PERMISSIONS.USER_PROFILE_UPDATE_PASSWORD,
  PERMISSIONS.WOUND_ASSESSMENT_WITHOUT_PHOTO,
  PERMISSIONS.RISK_ASSESSMENT,
  PERMISSIONS.UNABLE_TO_ACCESS_WOUND,
  PERMISSIONS.REGISTER_PATIENT_FOR_MOBILE,
  PERMISSIONS.ASSESSMENT_NOTE_DELETE,
  PERMISSIONS.ASSESSMENT_NOTE_EDIT,
  PERMISSIONS.NOTIFICATION_EDIT,
  PERMISSIONS.ADT_EDIT,
  PERMISSIONS.ANATOMY_EDIT,
  PERMISSIONS.CHARACTERISTIC_HISTORY_EDIT,
  PERMISSIONS.CHARACTERISTIC_TISSUE_EDIT,
  PERMISSIONS.CHARACTERISTIC_TOUCH_EDIT,
  PERMISSIONS.CHARACTERISTIC_CUSTOM_FORM_EDIT,
  PERMISSIONS.CHARACTERISTIC_CLASSIFICATION_EDIT,
  PERMISSIONS.CHARACTERISTIC_PROGRESS_EDIT,
  PERMISSIONS.MEASUREMENT_EDIT,
  PERMISSIONS.TREATMENT_EDIT,
  PERMISSIONS.CUSTOMER_SALES_AREA_EDIT,
];

/** Every legacy key except EXT_SIGN_LOCK - verbatim port of the original ALL_REQUIRED_PERMISSION. */
const ALL_REQUIRED_PERMISSION = LEGACY_PERMISSION_KEYS.filter((key) => key !== PERMISSIONS.EXT_SIGN_LOCK);

/** Every legacy key except CUSTOMER_SALES_AREA_EDIT - verbatim port of the original EXTERNAL_USER_PERMISSION. */
const EXTERNAL_USER_PERMISSION = LEGACY_PERMISSION_KEYS.filter((key) => key !== PERMISSIONS.CUSTOMER_SALES_AREA_EDIT);

/** Verbatim port of the original EXTERNAL array shared by the three NE1 external roles. */
const EXTERNAL: PermissionKey[] = [
  PERMISSIONS.PATIENT_ROUTE,
  PERMISSIONS.REPORT_ROUTE,
  PERMISSIONS.PATIENT,
  PERMISSIONS.ADD_PATIENT,
  PERMISSIONS.WOUND_ASSESSMENT_WITHOUT_PHOTO,
  PERMISSIONS.RISK_ASSESSMENT,
  PERMISSIONS.UNABLE_TO_ACCESS_WOUND,
  PERMISSIONS.REGISTER_PATIENT_FOR_MOBILE,
  PERMISSIONS.ADT_EDIT,
  PERMISSIONS.RE_EVAL,
  PERMISSIONS.CUSTOM_LABEL,
  PERMISSIONS.ANATOMY_EDIT,
  PERMISSIONS.ASSESSMENT,
  PERMISSIONS.CHARACTERISTIC,
  PERMISSIONS.CHARACTERISTIC_HISTORY_EDIT,
  PERMISSIONS.CHARACTERISTIC_TISSUE_EDIT,
  PERMISSIONS.CHARACTERISTIC_TOUCH_EDIT,
  PERMISSIONS.CHARACTERISTIC_CUSTOM_FORM_EDIT,
  PERMISSIONS.CHARACTERISTIC_CLASSIFICATION_EDIT,
  PERMISSIONS.CHARACTERISTIC_PROGRESS_EDIT,
  PERMISSIONS.MEASUREMENT,
  PERMISSIONS.MEASUREMENT_BUTTON,
  PERMISSIONS.MEASUREMENT_EDIT,
  PERMISSIONS.TREATMENT,
  PERMISSIONS.TREATMENT_SEARCH,
  PERMISSIONS.TREATMENT_ADD_INTERVENTION,
  PERMISSIONS.TREATMENT_EDIT,
  PERMISSIONS.NOTES,
  PERMISSIONS.ASSESSMENT_NOTE_DELETE,
  PERMISSIONS.NOTIFICATION_EDIT,
  PERMISSIONS.USER_PROFILE,
  PERMISSIONS.USER_PROFILE_UPDATE_PASSWORD,
];

const MEDLINE_ADMINISTRATOR_ADMIN_AREAS: PermissionKey[] = [
  PERMISSIONS.ADMIN_FORM_BUILDER,
  PERMISSIONS.ADMIN_IN_APP_MESSAGING,
  PERMISSIONS.ADMIN_MOBILE_APP_MGMT,
  PERMISSIONS.ADMIN_PLATFORM_ANALYTICS,
  PERMISSIONS.ADMIN_USAGE_ANALYTICS,
  PERMISSIONS.USAGE_ANALYTICS_CUSTOMER_GROUP_FILTER,
  PERMISSIONS.ADMIN_SURVEY_RESULT,
  PERMISSIONS.ADMIN_CONTEXTUAL_SUPPORT,
  PERMISSIONS.ADMIN_PRODUCTS,
  PERMISSIONS.ADMIN_CUSTOMERS,
  PERMISSIONS.ADMIN_FORMULARY,
  PERMISSIONS.ADMIN_PATIENTS,
  PERMISSIONS.ADMIN_USERS,
  PERMISSIONS.ADMIN_SSO,
  PERMISSIONS.ADMIN_DASHBOARD_SECTION,
  PERMISSIONS.ADMIN_MASTER_DATA_SECTION,
];

const MEDLINE_STANDARD_USER_ADMIN_AREAS: PermissionKey[] = [
  PERMISSIONS.ADMIN_FORMULARY,
  PERMISSIONS.ADMIN_MASTER_DATA_SECTION,
];

const MEDLINE_SUPPORT_ADMIN_AREAS: PermissionKey[] = [
  PERMISSIONS.ADMIN_PLATFORM_ANALYTICS,
  PERMISSIONS.ADMIN_USAGE_ANALYTICS,
  PERMISSIONS.USAGE_ANALYTICS_CUSTOMER_GROUP_FILTER,
  PERMISSIONS.ADMIN_PRODUCTS,
  PERMISSIONS.ADMIN_CUSTOMERS,
  PERMISSIONS.ADMIN_FORMULARY,
  PERMISSIONS.ADMIN_USERS,
  PERMISSIONS.ADMIN_PATIENTS,
  PERMISSIONS.ADMIN_CONTEXTUAL_SUPPORT,
  PERMISSIONS.ADMIN_DASHBOARD_SECTION,
  PERMISSIONS.ADMIN_MASTER_DATA_SECTION,
];

const EXTERNAL_ADMINISTRATOR_ADMIN_AREAS: PermissionKey[] = [
  PERMISSIONS.ADMIN_USAGE_ANALYTICS,
  PERMISSIONS.ADMIN_USERS,
  PERMISSIONS.ADMIN_PATIENTS,
  PERMISSIONS.ADMIN_CUSTOMERS,
  PERMISSIONS.ADMIN_DASHBOARD_SECTION,
];

const ADMIN_LIST_ACTIONS: PermissionKey[] = [PERMISSIONS.ADMIN_LIST_BULK_DELETE, PERMISSIONS.ADMIN_LIST_IMPORT];

const PATIENT_SUBMITTED_WOUND_PHOTO_ACTIONS: PermissionKey[] = [PERMISSIONS.PATIENT_SUBMITTED_WOUND_PHOTO_MANAGE];

const WOUND_ASSESSMENT_NO_PHOTO_CAUSE_STEP: PermissionKey[] = [PERMISSIONS.WOUND_ASSESSMENT_NO_PHOTO_CAUSE_STEP];

const WOUND_ASSESSMENT_NEW_WOUND: PermissionKey[] = [PERMISSIONS.WOUND_ASSESSMENT_NEW_WOUND];

const PATIENT_MANUAL_CREATE: PermissionKey[] = [PERMISSIONS.PATIENT_MANUAL_CREATE];

const ADMIN_FORMULARY_BEST_PRACTICE_EDIT: PermissionKey[] = [PERMISSIONS.ADMIN_FORMULARY_BEST_PRACTICE_EDIT];

const ADMIN_PATIENT_EDIT: PermissionKey[] = [PERMISSIONS.ADMIN_PATIENT_EDIT];

const CUSTOMER_PRIMARY_GROUP_ORG_EDIT: PermissionKey[] = [PERMISSIONS.CUSTOMER_PRIMARY_GROUP_ORG_EDIT];

const CUSTOMER_EXTENDED_DETAILS: PermissionKey[] = [PERMISSIONS.CUSTOMER_EXTENDED_DETAILS];

const CLINICAL_WRITE_PERMISSIONS: PermissionKey[] = [PERMISSIONS.ASSESSMENT_NOTE_CREATE];

export const ROLE_PERMISSIONS: Record<Role, PermissionKey[]> = {
  MedlineAdministrator: [...ALL_REQUIRED_PERMISSION, ...MEDLINE_ADMINISTRATOR_ADMIN_AREAS, ...ADMIN_LIST_ACTIONS, ...PATIENT_SUBMITTED_WOUND_PHOTO_ACTIONS, ...WOUND_ASSESSMENT_NO_PHOTO_CAUSE_STEP, ...WOUND_ASSESSMENT_NEW_WOUND, ...PATIENT_MANUAL_CREATE, ...ADMIN_FORMULARY_BEST_PRACTICE_EDIT, ...ADMIN_PATIENT_EDIT, ...CUSTOMER_PRIMARY_GROUP_ORG_EDIT, ...CUSTOMER_EXTENDED_DETAILS, ...CLINICAL_WRITE_PERMISSIONS, PERMISSIONS.ASSESSMENT_EXTERNAL_OWN_CUSTOMER],
  MedlineStandardUser: [...ALL_REQUIRED_PERMISSION, ...MEDLINE_STANDARD_USER_ADMIN_AREAS, ...ADMIN_LIST_ACTIONS, ...PATIENT_SUBMITTED_WOUND_PHOTO_ACTIONS, ...WOUND_ASSESSMENT_NO_PHOTO_CAUSE_STEP, ...WOUND_ASSESSMENT_NEW_WOUND, ...PATIENT_MANUAL_CREATE, ...CUSTOMER_PRIMARY_GROUP_ORG_EDIT, ...CUSTOMER_EXTENDED_DETAILS, ...CLINICAL_WRITE_PERMISSIONS, PERMISSIONS.ASSESSMENT_EXTERNAL_OWN_CUSTOMER],
  MedlineSupport: [...ALL_REQUIRED_PERMISSION, ...MEDLINE_SUPPORT_ADMIN_AREAS, ...ADMIN_LIST_ACTIONS, ...PATIENT_SUBMITTED_WOUND_PHOTO_ACTIONS, ...WOUND_ASSESSMENT_NO_PHOTO_CAUSE_STEP, ...WOUND_ASSESSMENT_NEW_WOUND, ...PATIENT_MANUAL_CREATE, ...CUSTOMER_PRIMARY_GROUP_ORG_EDIT, ...CUSTOMER_EXTENDED_DETAILS, ...CLINICAL_WRITE_PERMISSIONS, PERMISSIONS.ASSESSMENT_EXTERNAL_OWN_CUSTOMER],
  ExternalAdministrator: [...EXTERNAL_USER_PERMISSION, ...EXTERNAL_ADMINISTRATOR_ADMIN_AREAS, ...PATIENT_SUBMITTED_WOUND_PHOTO_ACTIONS, ...WOUND_ASSESSMENT_NO_PHOTO_CAUSE_STEP, ...WOUND_ASSESSMENT_NEW_WOUND, ...PATIENT_MANUAL_CREATE, ...ADMIN_PATIENT_EDIT, ...CLINICAL_WRITE_PERMISSIONS, PERMISSIONS.ASSESSMENT_EXTERNAL_OWN_CUSTOMER],
  ExternalStandardUser: [
    PERMISSIONS.WORKLIST_ROUTE,
    PERMISSIONS.PATIENT_ROUTE,
    PERMISSIONS.REPORT_ROUTE,
    PERMISSIONS.PATIENT,
    PERMISSIONS.ADD_PATIENT,
    PERMISSIONS.RISK_ASSESSMENT,
    PERMISSIONS.UNABLE_TO_ACCESS_WOUND,
    PERMISSIONS.ADT_EDIT,
    PERMISSIONS.RE_EVAL,
    PERMISSIONS.USER_PROFILE,
    PERMISSIONS.USER_PROFILE_UPDATE_PASSWORD,
    PERMISSIONS.MEASUREMENT,
    PERMISSIONS.MEASUREMENT_BUTTON,
    PERMISSIONS.MEASUREMENT_EDIT,
    PERMISSIONS.NOTES,
    PERMISSIONS.ASSESSMENT_NOTE_DELETE,
    ...CUSTOMER_PRIMARY_GROUP_ORG_EDIT,
    ...CUSTOMER_EXTENDED_DETAILS,
    ...CLINICAL_WRITE_PERMISSIONS,
  ],
  ExternalNE1WoundExpert: [
    ...EXTERNAL,
    ...PATIENT_SUBMITTED_WOUND_PHOTO_ACTIONS,
    ...WOUND_ASSESSMENT_NO_PHOTO_CAUSE_STEP,
    ...WOUND_ASSESSMENT_NEW_WOUND,
    ...CUSTOMER_PRIMARY_GROUP_ORG_EDIT,
    ...CUSTOMER_EXTENDED_DETAILS,
    ...CLINICAL_WRITE_PERMISSIONS,
    PERMISSIONS.WORKLIST_ROUTE,
    PERMISSIONS.TRANSFER_WOUND_ASSESSMENT_ROUTE,
    PERMISSIONS.MERGE_WOUND_ASSESSMENT_ROUTE,
    PERMISSIONS.ASSESSMENT_THREE_DOT_MENU,
    PERMISSIONS.LOCK,
    PERMISSIONS.SIGN,
    PERMISSIONS.ASSESSMENT_EXTERNAL_OWN_CUSTOMER,
  ],
  ExternalNE1Review: [...EXTERNAL, ...PATIENT_SUBMITTED_WOUND_PHOTO_ACTIONS, ...WOUND_ASSESSMENT_NO_PHOTO_CAUSE_STEP, ...WOUND_ASSESSMENT_NEW_WOUND, ...CUSTOMER_PRIMARY_GROUP_ORG_EDIT, ...CUSTOMER_EXTENDED_DETAILS, ...CLINICAL_WRITE_PERMISSIONS, PERMISSIONS.WORKLIST_ROUTE, PERMISSIONS.EXT_SIGN_LOCK, PERMISSIONS.ASSESSMENT_EXTERNAL_OWN_CUSTOMER],
  ExternalNE1Assesment: [...EXTERNAL, ...PATIENT_SUBMITTED_WOUND_PHOTO_ACTIONS, ...WOUND_ASSESSMENT_NO_PHOTO_CAUSE_STEP, ...WOUND_ASSESSMENT_NEW_WOUND, ...CUSTOMER_PRIMARY_GROUP_ORG_EDIT, ...CUSTOMER_EXTENDED_DETAILS, ...CLINICAL_WRITE_PERMISSIONS, PERMISSIONS.WORKLIST_ROUTE, PERMISSIONS.SIGN, PERMISSIONS.ASSESSMENT_EXTERNAL_OWN_CUSTOMER],
  ExternalNE1ReadOnly: [
    PERMISSIONS.PATIENT_ROUTE,
    PERMISSIONS.REPORT_ROUTE,
    PERMISSIONS.PATIENT,
    PERMISSIONS.ASSESSMENT,
    PERMISSIONS.CHARACTERISTIC,
    PERMISSIONS.MEASUREMENT,
    PERMISSIONS.MEASUREMENT_BUTTON,
    PERMISSIONS.TREATMENT,
    PERMISSIONS.NOTES,
    PERMISSIONS.USER_PROFILE,
    PERMISSIONS.USER_PROFILE_UPDATE_PASSWORD,
    ...PATIENT_SUBMITTED_WOUND_PHOTO_ACTIONS,
    ...WOUND_ASSESSMENT_NO_PHOTO_CAUSE_STEP,
    ...WOUND_ASSESSMENT_NEW_WOUND,
    ...CUSTOMER_PRIMARY_GROUP_ORG_EDIT,
    ...CUSTOMER_EXTENDED_DETAILS,
    PERMISSIONS.ASSESSMENT_EXTERNAL_OWN_CUSTOMER,
  ],
};
